import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  Button,
  Grid,
  Backdrop,
  IconButton,
} from "@mui/material";
import Iconify from "../Iconify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useStyles } from "./styles";
import { AppUser } from "../../pages/api";
import {
  getRecognition,
  getVoiceSearchLanguages,
  getVSLanguageCode,
} from "./utils";

interface IProps {
  label?: string | null;
  onSearch?: (query: string) => void;
  onOk: (text: string) => void;
  onClose: () => void;
  getSuggestions?: (query: string) => string[];
  placeholder?: string;
  languages?: { code: string; label: string; icon?: string }[];
}

export default ({
  label = "",
  onSearch,
  onOk,
  onClose,
  getSuggestions,
  placeholder = "",
  languages = [],
}: IProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const appUser = useSelector((state: RootState) => state.appUser);

  return (
    <>
      <Backdrop open={true} className={classes.backdrop} onClick={onClose} />
      <Grid className={classes.container}>
        <Search
          t={t}
          classes={classes}
          appUser={appUser}
          label={label}
          onSearch={onSearch}
          onOk={onOk}
          onClose={onClose}
          placeholder={
            placeholder?.length > 0
              ? placeholder
              : t("PressMicAndStartSpeaking") || ""
          }
          languages={
            languages?.length > 0 ? languages : getVoiceSearchLanguages(t)
          }
          getSuggestions={getSuggestions}
        />

        <style>
          {`
          @keyframes popupFade {
            0% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
            100% { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
        `}
        </style>
      </Grid>
    </>
  );
};

interface ISearchProps extends IProps {
  t: any;
  classes: any;
  appUser: AppUser;
}
const Search = ({
  t,
  classes,
  appUser,
  label,
  onSearch,
  onOk,
  onClose,
  getSuggestions,
  placeholder = "",
  languages = [],
}: ISearchProps) => {
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [language, setLanguage] = useState(
    getVSLanguageCode(t, appUser?.language)
  );
  // const [language, setLanguage] = useState(languages[0].code); // selected language
  const [rippleScale, setRippleScale] = useState(0.7);
  const [temp, setTemp] = useState<string>("");
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef<any>(null);
  const analyserRef: any = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const targetScaleRef = useRef(0.7);
  const inputRef = useRef<HTMLInputElement>(null);
  const isListeningRef = useRef(false);

  // Lerp helper
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  useEffect(() => {
    if (!recognitionRef.current) {
      const SpeechRecognition = getRecognition();

      if (!SpeechRecognition) {
        setError(t("YourBrowserDoesNotSupportVoiceSearch"));
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.lang = language; // use selected language

      recognition.onstart = () => {
        if (!isListeningRef.current) {
          isListeningRef.current = true;
          setIsListening(true);
          initAudio();
          animateRipple();
        }
      };
      recognition.onend = () => {
        isListeningRef.current = false;
        setIsListening(false);
        stopAudio();
        targetScaleRef.current = 0.7;
        setRippleScale(0.7);
      };

      recognition.onerror = (event: any) => {
        console.error(event.error);
        setError(t("VoiceRecognitionError"));
        isListeningRef.current = false;
        setIsListening(false);
        stopAudio();
      };

      recognition.onresult = (event: any) => {
        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += transcript;
          else interimTranscript += transcript;
        }

        const currentQuery = finalTranscript || interimTranscript;
        // setQuery(currentQuery);
        if (finalTranscript) {
          // setQuery((prev) => (prev ? `${prev} ${currentQuery}` : currentQuery));

          setTemp("");

          let cursorPosition: any = inputRef.current?.selectionStart;
          let next = "";
          setQuery((prev) => {
            next = prev;
            if (!prev) {
              cursorPosition = 0;
              next = currentQuery;
            } else {
              cursorPosition = inputRef.current?.selectionStart;
              if (cursorPosition == null || cursorPosition < 0) {
                cursorPosition = 0;
                next = `${prev}${currentQuery}`;
              } else {
                const before = prev.slice(0, cursorPosition);
                const after = prev.slice(cursorPosition);
                next = `${before}${currentQuery} ${after}`;
              }
            }
            return next;
          });

          requestAnimationFrame(() => {
            const newPos = cursorPosition + currentQuery?.length;
            inputRef.current?.setSelectionRange(newPos, newPos);
          });
          if (getSuggestions) setSuggestions(getSuggestions(next));

          if (onSearch) onSearch(finalTranscript);
        } else {
          setTemp(currentQuery);
        }
        // if (getSuggestions) setSuggestions(getSuggestions(currentQuery));
        // if (finalTranscript && onSearch) onSearch(finalTranscript);
      };

      recognitionRef.current = recognition;
      startAudio();
    }
    // }, [onSearch, getSuggestions, language]);
  }, []);

  const initAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray: any = new Uint8Array(bufferLength);

      source.connect(analyser);
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      const updateVolume = () => {
        if (
          !analyserRef.current ||
          !dataArrayRef.current ||
          !isListeningRef.current
        )
          return;
        analyserRef.current.getByteTimeDomainData(dataArrayRef?.current);
        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          const val = dataArrayRef.current[i] - 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArrayRef.current.length);
        targetScaleRef.current = 0.7 + Math.min(rms / 30, 0.9); // Map rms to 0.7-1.6
        requestAnimationFrame(updateVolume);
      };
      updateVolume();
    } catch (err) {
      console.error(err);
    }
  };

  const startAudio = () => {
    // document.body.click();
    recognitionRef.current.start();
  };

  const stopAudio = () => {
    analyserRef.current = null;
    dataArrayRef.current = null;
  };

  const animateRipple = () => {
    const update = () => {
      setRippleScale((prev) => lerp(prev, targetScaleRef.current, 0.1));
      if (isListeningRef.current) requestAnimationFrame(update);
    };
    requestAnimationFrame(update);
  };

  const restart = () => {
    if (!recognitionRef.current) return;
    setError(null);
    try {
      if (isListeningRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (ex) {}

        setTimeout(() => {
          try {
            startAudio();
          } catch (ex) {}
        }, 500);
      } else {
        startAudio();
      }
    } catch (ex: any) {
      // isListeningRef.current = true;
      // setIsListening(true);
    }
  };

  const handleMicClick = () => {
    if (!recognitionRef.current) return;
    setError(null);
    if (isListeningRef.current) {
      recognitionRef.current.stop();
    } else {
      try {
        startAudio();
      } catch (ex: any) {
        isListeningRef.current = true;
        setIsListening(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (getSuggestions) setSuggestions(getSuggestions(value));
    // handleSelect(e);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setQuery(suggestion);
    setSuggestions([]);
    if (onSearch) onSearch(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === "Enter") {
      if (highlightIndex >= 0)
        handleSelectSuggestion(suggestions[highlightIndex]);
      else if (onSearch) onSearch(query);
      setHighlightIndex(-1);
      setSuggestions([]);
    }
  };

  const highlightMatch = (text: string) => {
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.substring(0, index)}
        <strong>{text.substring(index, index + query?.length)}</strong>
        {text.substring(index + query?.length)}
      </>
    );
  };

  const handleLangChange = (e: any) => {
    setLanguage(e.target.value);
    recognitionRef.current.lang = e.target.value;
    restart();
  };

  const handleClear = () => {
    setTemp("");
    setQuery("");
  };

  return (
    <div
      className={isListening ? "listening" : "idle"}
      style={{ position: "relative" }}
    >
      <IconButton
        edge="start"
        color="inherit"
        onClick={onClose}
        aria-label="close"
        className={classes.closeButton}
        size="small"
      >
        <Iconify icon={"ep:close-bold"} width={20} sx={{ color: "#FF4842" }} />
      </IconButton>

      <Grid className={classes.tempText}>{temp ? temp : ""}</Grid>

      <TextField
        inputRef={inputRef}
        fullWidth
        label={label}
        variant="outlined"
        size="small"
        placeholder={isListeningRef.current ? t("SpeakNow") : placeholder}
        value={query}
        multiline={true}
        maxRows={15}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <div onClick={handleMicClick} className={classes.micContainer}>
                {isListeningRef.current && (
                  <>
                    <span
                      className="volume-ripple"
                      style={{
                        transform: `translate(-50%, -50%) scale(${rippleScale})`,
                      }}
                    />
                    <span
                      className="volume-ripple"
                      style={{
                        transform: `translate(-50%, -50%) scale(${
                          rippleScale * 0.85
                        })`,
                      }}
                    />
                    <span
                      className="volume-ripple"
                      style={{
                        transform: `translate(-50%, -50%) scale(${
                          rippleScale * 0.7
                        })`,
                      }}
                    />
                  </>
                )}
                <Iconify
                  icon={"ic:round-mic"}
                  width={28}
                  sx={{ color: isListeningRef.current ? "#f50057" : "#555" }}
                />
              </div>
            </InputAdornment>
          ),
        }}
        helperText={error || ""}
        error={!!error}
      />
      <Grid className={classes.footerContainer}>
        <FormControl>
          <Select
            value={language}
            onChange={handleLangChange}
            size="small"
            renderValue={(selected) => {
              const lang = languages.find((l) => l.code === selected);
              if (!lang) return selected;
              return (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {lang.icon && (
                    <img
                      src={lang.icon}
                      alt={lang.label}
                      style={{ width: 20, height: 14, objectFit: "cover" }}
                    />
                  )}
                  {lang.label}
                </span>
              );
            }}
          >
            {languages.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.icon && (
                  <img
                    src={lang.icon}
                    alt={lang.label}
                    className={classes.mapImage}
                  />
                )}
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <span>
          <Button
            onClick={() => onOk(query)}
            variant="contained"
            size="small"
            sx={{ mr: 1.5 }}
          >
            {t("Ok")}
          </Button>
          <Button onClick={handleClear} color="secondary" size="small">
            {t("Clear")}
          </Button>
          {/* <Button onClick={onClose} color="error" size="small">
            {t("Cancel")}
          </Button> */}
        </span>
      </Grid>

      {suggestions.length > 0 && (
        <Paper className={classes.sugContainer}>
          <List>
            {suggestions.map((suggestion, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  selected={index === highlightIndex}
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <ListItemText primary={highlightMatch(suggestion)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </div>
  );
};
